import { useMemo } from "react";
import { useMyUserGroups } from "../../../hooks/useUserGroups";
import {
  PurchaseRequestStatus,
  PurchaseRequestV2,
  UserResponse,
} from "../../../types";

export interface DetailPermissions {
  isRequestOwner: boolean;
  isAssignedBuyer: boolean;
  isEditableState: boolean;
  canEditRequest: boolean;
  canBuyerReject: boolean;
}

/**
 * The detail screen's role gating, extracted verbatim from the pre-Carbon `PurchaseRequestV2Detail`
 * (Task 5 migration). Preserves every rule exactly, including the deliberate separation the
 * pre-Carbon comment called out: `isRequestOwner` stays strictly "this is my own request" so the
 * `isAssignedBuyer` computation (which depends on it) is unaffected, while `canEditRequest` widens
 * to user-group peers of the request's owner.
 */
export const useDetailPermissions = (
  request: PurchaseRequestV2 | undefined,
  user: UserResponse | null | undefined,
  isRequester: boolean
): DetailPermissions => {
  const { data: myGroups } = useMyUserGroups(isRequester);

  const peerIds = useMemo(
    () => new Set(myGroups?.flatMap((g) => g.members.map((m) => m.id)) ?? []),
    [myGroups]
  );

  return useMemo(() => {
    const isRequestOwner =
      !!user && !!request && user.id === request.requester?.id && isRequester;

    const isEditableState =
      !!request &&
      [
        PurchaseRequestStatus.DRAFT,
        PurchaseRequestStatus.IN_APPROVAL,
        PurchaseRequestStatus.REJECTED,
      ].includes(request.overallStatus);

    const buyerItems =
      request && user
        ? request.items.filter(
            (item) => !item.deleted && item.assignedBuyer?.id === user.id
          )
        : [];

    const isAssignedBuyer = buyerItems.length > 0 && !isRequestOwner;

    const canRequesterEdit =
      isRequestOwner ||
      (isRequester &&
        !!request?.requester?.id &&
        peerIds.has(request.requester.id));

    // A draft belongs to whoever made it, whatever their role: a manager who imports a
    // spreadsheet gets drafts in their own name and has to be able to finish and send them.
    const isDraftOwner =
      !!user && !!request?.draft && user.id === request.requester?.id;

    const canEditRequest =
      (canRequesterEdit && isEditableState) || isDraftOwner;

    const canBuyerReject =
      isAssignedBuyer &&
      request?.overallStatus === PurchaseRequestStatus.IN_APPROVAL;

    return {
      isRequestOwner,
      isAssignedBuyer,
      isEditableState,
      canEditRequest,
      canBuyerReject,
    };
  }, [request, user, isRequester, peerIds]);
};
